import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateUserDetails } from '../../CreateUser/createUserSlice';
import { changeNewPassword } from '../../Login/loginSlice';
import MultiSelectDDL from '../inputs/MultiSelectDropdown';
import { getAssignRole } from '../../AdminUser/adminuserslice';
import Tooltip from '../Tooltip';
import { Admin } from '../../../sharedfiles/tooltipMsgs';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Switch } from '../../../ui/switch';
import { Button } from '../../../ui/button';

/**
 * EditUser - Migrated to shadcn/ui
 *
 * Complex dialog content for editing user details including:
 * - First name, last name, password fields with validation
 * - Role assignment (multi-select dropdown)
 * - TOTP enable/disable toggle
 * - Force password reset toggle
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Dialog open state (unused in content, managed by parent)
 * @param {Function} props.close - Callback to close dialog
 * @param {Object} props.userDetails - User details including roles array
 * @param {boolean} props.rolesRequired - Whether to show role assignment section
 * @param {string} props.page - Current page identifier for redux action
 * @param {number} props.userId - ID of user being edited
 * @param {Array} props.rolesList - Available roles for assignment
 *
 * Migration Notes:
 * - Replaced MUI FormGroup/Switch with shadcn Switch + Label
 * - Replaced custom input fields with shadcn Input + Label
 * - Uses Tooltip instead of legacy Tooltips
 * - Maintained all Redux integration (useDispatch, useSelector)
 * - Preserved all validation logic unchanged
 * - Uses shadcn Button for submit action
 * - Kept MultiSelectDDL (legacy) for role assignment (complex component, migrate separately)
 */

const formFieldData = [
  {
    id: 1,
    label: 'First Name',
    name: 'firstName',
    placeholder: 'First Name',
    errorMsg: 'First Name cannot be empty',
    regex: '',
    info: 'Need to give Users First Name'
  },
  {
    id: 2,
    label: 'Last Name',
    name: 'lastName',
    placeholder: 'Last Name',
    errorMsg: 'Last Name cannot be empty',
    regex: '',
    info: 'Need to give Users Last Name'
  },
  {
    id: 3,
    label: 'New Password',
    name: 'password',
    placeholder: 'Password',
    errorMsg: 'Password cannot be empty',
    regex: '',
    info: 'Need to set a new password'
  },
];

function EditUser(props) {
  const { open, close, userDetails, rolesRequired, page } = props;
  const dispatch = useDispatch();
  const selectedUserDetails = useSelector(state => state.createuserSlice.selectedUser);
  const permissionToEditUserDetails = useSelector(state => state.dashboard.permissionToEditUserDetails);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
  });

  const [formErrors, setFormErrors] = useState({
    firstName: '',
    lastName: '',
    password: '',
  });

  const [totpSwitch, setTotpSwitch] = useState(false);
  const [forcePinChange, setForcePinChange] = useState(false);
  const [ids, setIds] = useState([]);
  const [selected, setSelected] = useState([]);
  const [clearAll, setClearAll] = useState(false);
  const [permissionError, setPermissionError] = useState('');
  const [existingRoles, setExistingRoles] = useState([]);
  const [totpState, setTotpState] = useState('');

  // Validate role selection
  useEffect(() => {
    let mounting = true;
    if (mounting && rolesRequired) {
      if (ids.length === 0) {
        setPermissionError('Please select a proper Role');
      } else {
        setPermissionError('');
      }
    }
    return () => { mounting = false; };
  }, [ids, rolesRequired]);

  function formSetter(name, value) {
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  }

  function errorSetter(name, errorMsg) {
    setFormErrors((prevState) => ({ ...prevState, [name]: errorMsg }));
  }

  function validator(name, value) {
    let err = true;

    if (value === '') {
      let errName = name === 'firstName' ? 'First Name' : name === 'lastName' ? 'Last Name' : name;
      errorSetter(name, `${errName} cannot be empty`);
      err = false;
    } else {
      errorSetter(name, '');
      err = true;
    }

    if (name === 'password') {
      if (value === '') {
        errorSetter(name, '');
        err = true;
      } else if (value.length <= 7) {
        err = false;
        errorSetter(name, 'Must be atleast 8 characters!');
      } else if (!value.match(/^(?=.*?[A-Z])/)) {
        err = false;
        errorSetter(name, 'Must contain atleast 1 Letter in Capital Case!');
      } else if (!value.match(/^(?=.*?[a-z])/)) {
        err = false;
        errorSetter(name, 'Must contain atleast 1 Letter in Small Case!');
      } else if (!value.match(/^(?=.*?[0-9])/)) {
        err = false;
        errorSetter(name, 'Must contain atleast 1 number');
      } else if (!value.match(/^(?=.*?[-_@#$%^&*.])/)) {
        err = false;
        errorSetter(name, 'Must contain atleast 1 Special Character!');
      } else {
        err = true;
      }
    }
    return err;
  }

  const handleFormData = (e) => {
    const { name, value } = e.target;
    e.preventDefault();

    if (['firstName', 'lastName'].includes(name)) {
      if ((/^[a-zA-Z]*$/).test(value)) {
        formSetter(name, value);
        validator(name, value);
      }
    } else {
      if (!(/\s/.test(value))) {
        formSetter(name, value);
        validator(name, value);
      }
    }
  };

  function checkRolesDiff() {
    let driver = false;
    if (ids.length > 0) driver = true;
    if (existingRoles.length === ids.length && existingRoles?.every(role => ids.includes(role))) driver = false;
    return driver;
  }

  function submitForm(e) {
    e.preventDefault();
    const errs = Object.entries(formData).map(([name, value]) => validator(name, value));
    const clearTOGo = permissionToEditUserDetails ? errs.every(err => err === true) : true;
    const isToDisable = totpState === 'Disable';

    const userDetailsPayload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      forceChangePin: forcePinChange,
      forceTotp: totpSwitch ? isToDisable ? false : true : selectedUserDetails.forcedTotp,
      enableTotp: totpSwitch ? false : selectedUserDetails.totpEnable,
    };

    const passwordPayload = {
      password: formData.password,
      enableForceChangePin: true
    };

    const rolesPayload = {
      userId: props.userId,
      roles: ids,
    };

    if (clearTOGo) {
      permissionToEditUserDetails && dispatch(updateUserDetails(userDetailsPayload, props.userId, page));
      permissionToEditUserDetails && formData.password !== '' && dispatch(changeNewPassword(passwordPayload, props.userId));

      if (rolesRequired && checkRolesDiff()) {
        dispatch(getAssignRole(rolesPayload, true));
        close(false);
      }
      permissionToEditUserDetails && close(false);
    }
  }

  // Load existing roles
  useEffect(() => {
    let mounting = true;
    if (mounting && rolesRequired) {
      const existingRoles = Array.isArray(userDetails.roles) && userDetails.roles.length > 0
        ? userDetails.roles.map(role => role.roleId)
        : [];
      setExistingRoles(existingRoles);
      setIds(existingRoles);
    }
    return () => { mounting = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails, rolesRequired]);

  // Load selected user details
  useEffect(() => {
    let mounting = true;
    if (mounting) {
      setFormData((prevState) => ({
        ...prevState,
        firstName: selectedUserDetails.firstName,
        lastName: selectedUserDetails.lastName,
      }));
      setForcePinChange(selectedUserDetails.forceChangePin);
    }
    if (!selectedUserDetails.forceTotp && !selectedUserDetails.totpEnable) {
      setTotpState('Enable');
    }
    if (!selectedUserDetails.forceTotp && selectedUserDetails.totpEnable) {
      setTotpState('Disable');
    }
    return () => { mounting = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserDetails]);

  return (
    <div className="space-y-4 p-1">
      {/* Form Fields (First Name, Last Name, Password) */}
      {permissionToEditUserDetails &&
        formFieldData.map((item) => (
          <div key={item.id} className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor={item.name} className="text-sm font-medium">
                {item.label}
              </Label>
              <Tooltip info={item.info} />
            </div>
            <Input
              type="text"
              id={item.name}
              name={item.name}
              value={formData[item.name] || ''}
              placeholder={item.placeholder}
              onChange={handleFormData}
            />
            {formErrors[item.name] && (
              <p className="text-sm text-destructive">{formErrors[item.name]}</p>
            )}
          </div>
        ))
      }

      {/* Role Assignment */}
      {rolesRequired && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium">Assign Role</Label>
            <Tooltip info={Admin.Assign_Role} />
          </div>
          <MultiSelectDDL
            options={props.rolesList || []}
            onSelect={(values) => setIds(values)}
            initialSelected={existingRoles}
            shouldReset={clearAll}
          />
          {permissionError && (
            <p className="text-sm text-destructive">{permissionError}</p>
          )}
        </div>
      )}

      {/* TOTP and Force Pin Change Toggles */}
      {permissionToEditUserDetails && (
        <div className="space-y-4 pt-2">
          {/* TOTP Toggle */}
          {!selectedUserDetails?.forcedTotp && (
            <div className="flex items-center gap-3">
              <Switch
                checked={totpSwitch || false}
                onCheckedChange={(checked) => setTotpSwitch(checked)}
                id="totpSwitch"
              />
              <Label htmlFor="totpSwitch" className="text-base cursor-pointer">
                Do you want to {totpState} TOTP
              </Label>
            </div>
          )}

          {/* Force Pin Change Toggle */}
          <div className="flex items-center gap-3">
            <Switch
              checked={forcePinChange || false}
              onCheckedChange={(checked) => setForcePinChange(checked)}
              id="forcePin"
            />
            <Label htmlFor="forcePin" className="text-base cursor-pointer">
              Do you want to Enable Force reset Password
            </Label>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="pt-4">
        <Button
          type="button"
          onClick={submitForm}
          className="w-full"
        >
          Submit
        </Button>
      </div>
    </div>
  );
}

export default EditUser;